import { useEffect, useState } from "react";
import { Filtre, FiltreItem } from "@/types/filtres";
import { useRouter } from "next/router";
import useQueryString from "./useQueryString";

type useFiltersReturns = {
    filters: Filtre[];
    filtersSelected: Set<FiltreItem>;
    handleFilterSelection: (item: FiltreItem) => void;
    handleFilterRemoval: (item: FiltreItem) => void;
    handleFilterClear: () => void;
};

const useFilters = (): useFiltersReturns => {
    const [filters, setFilters] = useState<Filtre[]>([]);
    const [filtersSelected, setFiltersSelected] = useState<Set<FiltreItem>>(new Set());
    const { setQueryString, removeQueryParam } = useQueryString();
    const router = useRouter();

    const handleFilterSelection = (filter: FiltreItem) => {
        const newSelected = new Set(filtersSelected);
        if (newSelected.has(filter)) {
            newSelected.delete(filter);
        } else {
            newSelected.add(filter);
        }

        setFiltersSelected(newSelected);

        setQueryString({
            filtres: Array.from(newSelected).map((item) => item.id),
        });
    };

    const handleFilterRemoval = (filter: FiltreItem) => {
        const newSelected = new Set(filtersSelected);
        newSelected.delete(filter);
        setFiltersSelected(newSelected);
        setQueryString({
            filtres: Array.from(newSelected).map((item) => item.id),
        });
    };

    const handleFilterClear = () => {
        setFiltersSelected(new Set());
        removeQueryParam("filtres");
    };

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        fetch("/api/filtres", {
            signal: controller.signal,
        })
            .then((response) => response.json())
            .then((data: Filtre[]) => {
                if (isMounted) {
                    setFilters(data);
                }
            });

        return () => {
            isMounted = false;
            if (controller.signal.aborted) {
                controller.abort();
            }
        };
    }, []);

    useEffect(() => {
        const flatItems = filters.map((filter) => filter.items).flat();
        const filterQuery = router.query["filtres"];
        const selectedFilters = flatItems.filter((filter) =>
            Array.isArray(filterQuery)
                ? filterQuery?.includes(filter.id.toString())
                : filterQuery === filter.id.toString(),
        );
        setFiltersSelected(new Set(selectedFilters));
    }, [filters, router.query]);

    return {
        filters,
        filtersSelected,
        handleFilterSelection,
        handleFilterRemoval,
        handleFilterClear,
    };
};

export default useFilters;
